// SPDX-License-Identifier: AGPL-3.0-or-later
import KonvoVoicePlayer, {
	DEFAULT_KONVO_VOICE,
	isKonvoVoice,
} from '@app/features/konvo_assistant/state/KonvoVoicePlayer';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import UserSettings from '@app/features/user/state/UserSettings';
import VoiceTts from '@app/features/voice/utils/VoiceTtsUtils';
import {makeAutoObservable} from 'mobx';

const RATE_KEY = 'konvo.assistant.speechRate';
const VOICE_KEY = 'konvo.assistant.speechVoice';
const DICTATION_KEY = 'konvo.assistant.dictation';
const STORAGE_KEY = 'konvo.assistant.readRepliesAloud';
const ENGINE_KEY = 'konvo.assistant.voiceEngine';
const GEMINI_VOICE_KEY = 'konvo.assistant.geminiVoice';
const CODEX_VOICE_KEY = 'konvo.assistant.codexVoice';
const CODEX_MODE_KEY = 'konvo.assistant.codexVoiceMode';
const SEND_DELAY_KEY = 'konvo.assistant.voiceSendDelay';
const ENGINES_KEY = 'konvo.assistant.voiceEngines';
const PLACEMENT_KEY = 'konvo.assistant.voicePlacement';

/** Seconds before voice mode sends a prepared Konvo chat message on its own; 0 = always wait for "send it". */
export const VOICE_SEND_DELAYS = [0, 3, 5, 10] as const;
export type VoiceSendDelay = (typeof VOICE_SEND_DELAYS)[number];

function storedSendDelay(): VoiceSendDelay {
	const stored = Number(AppStorage.getItem(SEND_DELAY_KEY) ?? '5');
	return (VOICE_SEND_DELAYS as ReadonlyArray<number>).includes(stored) ? (stored as VoiceSendDelay) : 5;
}

/**
 * Where the voice mode globe sits: centred (above the phone rail, above the composer on desktop) or
 * in place of the Ask button (the rail's Ask pill on phones, the corner assistant on desktop).
 */
export type VoicePlacement = 'centre' | 'ask';

function storedPlacement(): VoicePlacement | null {
	const value = AppStorage.getItem(PLACEMENT_KEY);
	return value === 'centre' || value === 'ask' ? value : null;
}

/** Hearing and speaking provider; the automatic preference follows the Codex connection. */
export type VoiceEngine = 'konvo' | 'gemini' | 'codex-live' | 'codex-voice';
export type VoiceEnginePreference = VoiceEngine | 'automatic';
export interface VoiceEngineAvailability {
	gemini: boolean;
	codex: boolean;
	codex_connected?: boolean;
	codex_voices?: Array<string>;
	codex_default_voice?: string;
	gemini_voices?: Array<{id: string; style: string}>;
}
function storedEngine(): VoiceEnginePreference {
	const value = AppStorage.getItem(ENGINE_KEY);
	return ['konvo', 'gemini', 'codex-live', 'codex-voice'].includes(value ?? '') ? (value as VoiceEngine) : 'automatic';
}

/** What the server last said this person can use, so voice mode can start without asking first. */
function storedEngines(): VoiceEngineAvailability | null {
	try {
		const value = JSON.parse(AppStorage.getItem(ENGINES_KEY) ?? 'null') as Partial<VoiceEngineAvailability> | null;
		if (typeof value?.gemini !== 'boolean' || typeof value.codex !== 'boolean') return null;
		return {gemini: value.gemini, codex: value.codex, codex_connected: value.codex_connected === true};
	} catch {
		return null;
	}
}

export class AssistantSpeechStore {
	automatic = AppStorage.getItem(STORAGE_KEY) === 'true';
	currentId: string | null = null;
	paused = false;
	rate = Math.min(2, Math.max(0.5, Number(AppStorage.getItem(RATE_KEY)) || 1));
	voiceId = AppStorage.getItem(VOICE_KEY) ?? '';
	dictationEnabled = AppStorage.getItem(DICTATION_KEY) === 'true';
	voiceEnginePreference: VoiceEnginePreference = storedEngine();
	codexVoiceMode: 'codex-live' | 'codex-voice' =
		AppStorage.getItem(CODEX_MODE_KEY) === 'codex-voice' || this.voiceEnginePreference === 'codex-voice'
			? 'codex-voice'
			: 'codex-live';
	codexConnected = storedEngines()?.codex_connected ?? false;
	/** The voice engines this person had last time the server was asked. */
	knownVoiceEngines: VoiceEngineAvailability | null = storedEngines();
	codexVoice = AppStorage.getItem(CODEX_VOICE_KEY) ?? '';
	/** Gemini Live voice ('' = the server's default). */
	geminiVoice = AppStorage.getItem(GEMINI_VOICE_KEY) ?? '';
	voiceSendDelay: VoiceSendDelay = storedSendDelay();
	/** Chosen voice mode position on this device; null follows the layout (see voicePlacementFor). */
	voicePlacement: VoicePlacement | null = storedPlacement();
	error = false;
	/** Voice mode speaks its own replies, so the conversation's automatic read-aloud holds off meanwhile. */
	voiceModeActive = false;
	private queue: Array<{id: string; text: string}> = [];
	private generation = 0;
	private awaitingNext = false;
	private engine: 'konvo' | 'device' | null = null;
	// Set when the server has no speech service, so later replies go straight to the device voice.
	private konvoUnavailable = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	setAutomatic(value: boolean): void {
		this.automatic = value;
		AppStorage.setItem(STORAGE_KEY, String(value));
		if (!value) this.stop();
	}

	setVoiceModeActive(value: boolean): void {
		this.voiceModeActive = value;
		if (value) this.stop();
	}

	setRate(value: number): void {
		if (!Number.isFinite(value)) return;
		this.rate = Math.min(2, Math.max(0.5, value));
		AppStorage.setItem(RATE_KEY, String(this.rate));
	}

	setVoice(value: string): void {
		this.voiceId = value;
		AppStorage.setItem(VOICE_KEY, value);
	}

	get voiceEngine(): VoiceEngine {
		return this.voiceEnginePreference === 'automatic'
			? this.codexConnected
				? 'codex-live'
				: 'gemini'
			: this.voiceEnginePreference;
	}
	setVoiceEngine(value: VoiceEnginePreference): void {
		this.voiceEnginePreference = value;
		AppStorage.setItem(ENGINE_KEY, value);
		if (value === 'codex-live' || value === 'codex-voice') {
			this.codexVoiceMode = value;
			AppStorage.setItem(CODEX_MODE_KEY, value);
		}
	}
	setCodexVoice(value: string): void {
		this.codexVoice = value;
		AppStorage.setItem(CODEX_VOICE_KEY, value);
	}
	rememberVoiceEngines(availability: VoiceEngineAvailability): void {
		const known = {
			gemini: availability.gemini,
			codex: availability.codex,
			codex_connected: availability.codex_connected ?? availability.codex,
		};
		this.knownVoiceEngines = known;
		this.codexConnected = known.codex_connected;
		AppStorage.setItem(ENGINES_KEY, JSON.stringify(known));
	}
	resolveVoiceEngine(availability: VoiceEngineAvailability): VoiceEngine {
		this.codexConnected = availability.codex_connected ?? availability.codex;
		const engine = this.voiceEngine;
		if ((engine === 'codex-live' || engine === 'codex-voice') && !availability.codex)
			return availability.gemini ? 'gemini' : 'konvo';
		return engine === 'gemini' && !availability.gemini ? 'konvo' : engine;
	}

	setGeminiVoice(value: string): void {
		this.geminiVoice = value;
		AppStorage.setItem(GEMINI_VOICE_KEY, value);
	}

	setVoiceSendDelay(value: VoiceSendDelay): void {
		this.voiceSendDelay = value;
		AppStorage.setItem(SEND_DELAY_KEY, String(value));
	}

	/** Phones keep the globe centred over the rail, desktop turns the corner assistant into it, unless chosen. */
	voicePlacementFor(mobile: boolean): VoicePlacement {
		return this.voicePlacement ?? (mobile ? 'centre' : 'ask');
	}

	setVoicePlacement(value: VoicePlacement): void {
		this.voicePlacement = value;
		AppStorage.setItem(PLACEMENT_KEY, value);
	}

	setDictationEnabled(value: boolean): void {
		this.dictationEnabled = value;
		AppStorage.setItem(DICTATION_KEY, String(value));
	}

	// A saved Konvo voice wins; "Automatic" reads English in the default Konvo voice and
	// other languages in the device voice, since the Konvo voices only speak English.
	get effectiveVoiceId(): string {
		if (isKonvoVoice(this.voiceId)) return this.konvoUnavailable ? '' : this.voiceId;
		if (this.voiceId) return this.voiceId;
		return !this.konvoUnavailable && UserSettings.locale.toLowerCase().startsWith('en') ? DEFAULT_KONVO_VOICE : '';
	}

	togglePaused(): void {
		if (!this.currentId) return;
		this.paused = !this.paused;
		if (this.paused) {
			if (this.engine === 'konvo') KonvoVoicePlayer.pause();
			else VoiceTts.pause();
		} else if (this.awaitingNext) this.next();
		else if (this.engine === 'konvo') KonvoVoicePlayer.resume();
		else VoiceTts.resume();
	}

	read(id: string, text: string): void {
		if (this.currentId === id) {
			this.stop();
			return;
		}
		this.stop();
		this.enqueue(id, text);
	}

	enqueue(id: string, text: string): void {
		if (!text.trim() || this.currentId === id || this.queue.some((item) => item.id === id)) return;
		this.queue.push({id, text});
		if (!this.currentId && !this.paused) this.next();
	}

	private next(): void {
		if (this.paused) {
			this.awaitingNext = true;
			return;
		}
		this.awaitingNext = false;
		const item = this.queue.shift();
		if (!item) {
			this.currentId = null;
			return;
		}
		this.currentId = item.id;
		this.error = false;
		const generation = this.generation;
		const voiceId = this.effectiveVoiceId;
		if (isKonvoVoice(voiceId)) {
			this.engine = 'konvo';
			KonvoVoicePlayer.speak(item.text, {
				voiceId,
				rate: this.rate,
				onEnd: () => {
					if (generation === this.generation) this.next();
				},
				onError: (unread, unavailable) => {
					if (generation !== this.generation) return;
					if (unavailable) this.markKonvoUnavailable();
					this.speakOnDevice(unread, '', generation);
				},
			});
			return;
		}
		this.speakOnDevice(item.text, voiceId, generation);
	}

	private markKonvoUnavailable(): void {
		this.konvoUnavailable = true;
	}

	private speakOnDevice(text: string, voiceId: string, generation: number): void {
		this.engine = 'device';
		try {
			VoiceTts.speak(text, {
				rate: this.rate,
				voiceId,
				maxLength: Number.POSITIVE_INFINITY,
				onCancel: () => {
					if (generation === this.generation) this.stop();
				},
				onEnd: () => {
					if (generation === this.generation) this.next();
				},
				onError: () => {
					if (generation !== this.generation) return;
					this.stop();
					this.setError();
				},
			});
		} catch {
			this.stop();
			this.setError();
		}
	}

	private setError(): void {
		this.error = true;
	}

	stop(): void {
		this.generation += 1;
		this.queue = [];
		this.paused = false;
		this.awaitingNext = false;
		if (this.currentId) {
			if (this.engine === 'konvo') KonvoVoicePlayer.stop();
			else VoiceTts.stop();
		}
		this.engine = null;
		this.currentId = null;
		this.error = false;
	}
}

const AssistantSpeech = new AssistantSpeechStore();
if (typeof document !== 'undefined') {
	document.addEventListener('pointerdown', () => KonvoVoicePlayer.prime(), {once: true, capture: true});
}
AppStorage.subscribe(
	() => {
		AssistantSpeech.setAutomatic(AppStorage.getItem(STORAGE_KEY) === 'true');
	},
	{key: STORAGE_KEY, source: 'external'},
);
AppStorage.subscribe(() => AssistantSpeech.setRate(Number(AppStorage.getItem(RATE_KEY)) || 1), {
	key: RATE_KEY,
	source: 'external',
});
AppStorage.subscribe(() => AssistantSpeech.setVoice(AppStorage.getItem(VOICE_KEY) ?? ''), {
	key: VOICE_KEY,
	source: 'external',
});
AppStorage.subscribe(() => AssistantSpeech.setDictationEnabled(AppStorage.getItem(DICTATION_KEY) === 'true'), {
	key: DICTATION_KEY,
	source: 'external',
});
AppStorage.subscribe(() => AssistantSpeech.setVoiceEngine(storedEngine()), {key: ENGINE_KEY, source: 'external'});
AppStorage.subscribe(
	() => {
		const value = storedPlacement();
		if (value) AssistantSpeech.setVoicePlacement(value);
	},
	{key: PLACEMENT_KEY, source: 'external'},
);
AppStorage.subscribe(() => AssistantSpeech.setCodexVoice(AppStorage.getItem(CODEX_VOICE_KEY) ?? ''), {
	key: CODEX_VOICE_KEY,
	source: 'external',
});
export default AssistantSpeech;
